import type { FC } from 'react';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Badge, Card, Descriptions, Flex, Space, Tag, Typography } from 'antd';
import type { ColumnsType, TableProps } from 'antd/es/table';
import type { PlanResponseDto } from '@evimo/client/models';
import { useTranslation } from 'react-i18next';
import { Button } from '~/app/components/common/Button';
import type { PlanAssetRow, PlanScheduleRow, PlanTaskRow } from './types';
import { formatDate } from './utils';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';

const { Title, Paragraph, Text } = Typography;

const PRIORITY_COLOR: Record<string, string> = {
  HIGH: 'red',
  MEDIUM: 'orange',
  LOW: 'blue',
};

const CARD_STYLE = {
  borderRadius: 16,
  border: '1px solid var(--border-subtle, #e2e8f0)',
} as const;

export interface PlanDetailPageViewProps {
  plan: PlanResponseDto;
  taskRows: PlanTaskRow[];
  assetRows: PlanAssetRow[];
  scheduleRows: PlanScheduleRow[];
  taskColumns: ColumnsType<PlanTaskRow>;
  taskExpandable: TableProps<PlanTaskRow>['expandable'];
  assetColumns: ColumnsType<PlanAssetRow>;
  scheduleColumns: ColumnsType<PlanScheduleRow>;
  sectionError: string | null;
  assetsTotal: number;
  assetsPage: number;
  assetsPageSize: number;
  assetsLoading: boolean;
  onAssetsPageChange: (page: number) => void;
  schedulesTotal: number;
  schedulesPage: number;
  schedulesPageSize: number;
  schedulesLoading: boolean;
  onSchedulesPageChange: (page: number) => void;
  onBack: () => void;
}

export const PlanDetailPageView: FC<PlanDetailPageViewProps> = ({
  plan,
  taskRows,
  assetRows,
  scheduleRows,
  taskColumns,
  taskExpandable,
  assetColumns,
  scheduleColumns,
  sectionError,
  assetsTotal,
  assetsPage,
  assetsPageSize,
  assetsLoading,
  onAssetsPageChange,
  schedulesTotal,
  schedulesPage,
  schedulesPageSize,
  schedulesLoading,
  onSchedulesPageChange,
  onBack,
}) => {
  const { t } = useTranslation('maintenance');

  return (
    <Flex vertical gap="large" className="app-detail-container">
      <Flex vertical gap="small" className="app-detail-header">
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          className="app-detail-back-button"
          onClick={onBack}
        >
          {t('detail.back')}
        </Button>
        <Space align="center" size={12} wrap>
          <Title level={2} className="app-detail-title">
            {plan.name}
          </Title>
          <Tag>{plan.code}</Tag>
          <Tag color={PRIORITY_COLOR[plan.priority] ?? 'default'}>
            {t(`priority.${plan.priority}`)}
          </Tag>
          {plan.active ? (
            <Badge status="success" text={t('common.active')} />
          ) : (
            <Badge status="default" text={t('common.inactive')} />
          )}
        </Space>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          {t('detail.subtitle')}
        </Paragraph>
      </Flex>

      {sectionError ? <Text type="danger">{sectionError}</Text> : null}

      <Card title={t('detail.overview')} style={CARD_STYLE}>
        <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
          <Descriptions.Item label={t('plans.code')}>
            {plan.code}
          </Descriptions.Item>
          <Descriptions.Item label={t('plans.name')}>
            {plan.name}
          </Descriptions.Item>
          <Descriptions.Item label={t('plans.priority')}>
            <Tag color={PRIORITY_COLOR[plan.priority] ?? 'default'}>
              {t(`priority.${plan.priority}`)}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('plans.active')}>
            {plan.active ? (
              <Badge status="success" text={t('common.active')} />
            ) : (
              <Badge status="default" text={t('common.inactive')} />
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('plans.schedulingHorizonDays')}>
            {t('plans.schedulingHorizonDaysValue', {
              days: plan.schedulingHorizonDays,
            })}
          </Descriptions.Item>
          <Descriptions.Item label={t('common.createdAt')}>
            {formatDate(plan.createdAt)}
          </Descriptions.Item>
          <Descriptions.Item label={t('detail.updatedAt')}>
            {formatDate(plan.updatedAt)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card
        title={
          <Space size={8}>
            <span>{t('detail.tasks.sectionTitle')}</span>
            <Tag>{taskRows.length}</Tag>
          </Space>
        }
        style={CARD_STYLE}
        styles={{ body: { padding: 0 } }}
      >
        <EvimoTable<PlanTaskRow>
          className="pmp-tasks-with-subtasks"
          rowKey="id"
          columns={taskColumns}
          dataSource={taskRows}
          pagination={false}
          size="small"
          expandable={taskExpandable}
          locale={{ emptyText: t('detail.tasks.empty') }}
        />
      </Card>

      <Card
        title={
          <Space size={8}>
            <span>{t('detail.assets.sectionTitle')}</span>
            <Tag>{assetsTotal}</Tag>
          </Space>
        }
        style={CARD_STYLE}
        styles={{ body: { padding: 0 } }}
      >
        <EvimoTable<PlanAssetRow>
          rowKey="id"
          columns={assetColumns}
          dataSource={assetRows}
          loading={assetsLoading}
          pagination={{
            current: assetsPage,
            pageSize: assetsPageSize,
            total: assetsTotal,
            showSizeChanger: false,
            onChange: onAssetsPageChange,
          }}
          size="small"
          locale={{ emptyText: t('detail.assets.empty') }}
        />
      </Card>

      <Card
        title={
          <Space size={8}>
            <span>{t('detail.schedules.sectionTitle')}</span>
            <Tag>{schedulesTotal}</Tag>
          </Space>
        }
        style={CARD_STYLE}
        styles={{ body: { padding: 0 } }}
      >
        <EvimoTable<PlanScheduleRow>
          rowKey="id"
          columns={scheduleColumns}
          dataSource={scheduleRows}
          loading={schedulesLoading}
          pagination={{
            current: schedulesPage,
            pageSize: schedulesPageSize,
            total: schedulesTotal,
            showSizeChanger: false,
            onChange: onSchedulesPageChange,
          }}
          size="small"
          locale={{ emptyText: t('detail.schedules.empty') }}
        />
      </Card>
    </Flex>
  );
};
